/**
 * ExportModal — "Attach N leads to a study" (the run page's More ▾ action; plan
 * §7.5): a three-step stepper —
 * (1) Study: study + attach options (§6.6, D-5 fill-empty default),
 * (2) Message (collapsed by default): channel, the message itself (written here
 *     or started from a saved template, with the Variables menu and a preview as
 *     real people on the list will read it), start time, pace, who-won't-get-it
 *     checklist with LIVE preflight counts from {dry_run:true},
 * (3) Review: the full arithmetic restated before the irreversible-ish step.
 * After launch it renders progress + receipts from GET /search-exports/:id and
 * offers Cancel while campaign rows remain scheduled.
 *
 * The dry run leaves the message out, so typing never re-runs the counts: the
 * message is checked here (campaignMessage.ts, the server's rules) and again by
 * the server at launch, and the preview fills it in client-side from the people
 * the preflight returns.
 *
 * A campaign never sends from here: rows ride crm.scheduled_messages and the
 * worker re-runs every compliance gate at fire time (D-6).
 *
 * Words (docs/crm-outreach-lists-plan.md §A7): the server hands back codes and
 * developer-worded warnings; searchLib turns every one into a sentence —
 * skip reasons, cancel reasons, attach actions — and receipts name the
 * recruiter, never a user id.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Loader2, MessageSquare, Send, X } from 'lucide-react';
import api from '../../../services/api';
import type {
  ExportPreflight, ExportProgressInfo, ExportReceiptItem, MessageTemplate,
  SearchExport, SearchRunSummary, StudyOption, TeamMember,
} from '../../types';
import { dateTime, relTime } from '../../lib';
import { insertAtCursor } from '../../mergeFields';
import VariablePicker from '../VariablePicker';
import { Alert, Z } from '../ui';
import {
  EXPORT_SKIP_LABELS, attachActionsText, attachMoveLines, cancelReasonText, exportParamErrorText, exportSkipLabel,
  exportWarningText,
} from '../../pages/search/searchLib';
import {
  CAMPAIGN_VARIABLES, checkCampaignMessage, previewPersonName, renderCampaignMessage,
  type CampaignPreviewPerson,
} from './campaignMessage';

interface Props {
  run: SearchRunSummary;
  searchName: string | null;
  studies: StudyOption[];
  team: TeamMember[];
  onClose: () => void;
}

/** The server's plain sentence (`error`) — never its raw `detail`. */
const errMsg = (e: any): string => {
  const serverSentence = e?.response?.data?.error;
  if (typeof serverSentence === 'string' && serverSentence) return exportParamErrorText(serverSentence);
  return e?.response ? 'Something went wrong. Please try again.' : "Couldn't reach the server. Check your connection and try again.";
};

/** The export row's failure text: the reaper's "timed_out (…)" becomes what to do about it. */
const exportErrorText = (err: string): string =>
  (/^timed_out/i.test(err)
    ? 'This stopped partway through. Start it again and it will pick up where it left off.'
    : err);

/** One receipt as a pill + an optional detail line, in words. */
function receiptWords(it: ExportReceiptItem, teamName: (id: string) => string): { pill: string; detail: string | null; tone: string } {
  const tones = {
    primary: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    good: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    bad: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    muted: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  switch (it.outcome) {
    case 'attached': return { pill: 'Attached', detail: attachActionsText(it.detail), tone: tones.primary };
    case 'owner_assigned': return { pill: 'Owner set', detail: it.detail ? teamName(it.detail) : null, tone: tones.primary };
    case 'scheduled': {
      const m = it.message;
      if (m?.status === 'sent') return { pill: 'Sent', detail: m.sent_at ? dateTime(m.sent_at) : null, tone: tones.good };
      if (m?.status === 'canceled') return { pill: 'Not sent', detail: cancelReasonText(m.last_error), tone: tones.muted };
      if (m?.status === 'failed') return { pill: "Couldn't send", detail: cancelReasonText(m.last_error), tone: tones.bad };
      if (m?.status === 'sending') return { pill: 'Sending', detail: null, tone: tones.primary };
      return { pill: 'Scheduled', detail: m?.send_at ? dateTime(m.send_at) : null, tone: tones.primary };
    }
    case 'skipped': return { pill: 'Skipped', detail: exportSkipLabel(it.detail), tone: tones.muted };
    case 'failed': return { pill: 'Failed', detail: "Couldn't update this lead", tone: tones.bad };
    default: return { pill: 'Done', detail: null, tone: tones.muted };
  }
}

const fieldCls = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-300';
const checkCls = 'rounded border-gray-300 text-primary-600 focus:ring-primary-500';

const SKIP_LABELS = EXPORT_SKIP_LABELS;
/** Skips the server always applies (no toggle) — shown only once it reports a count for them. */
const ALWAYS_ON_SKIPS = ['wrong_person'];

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

const MESSAGE_PLACEHOLDER = {
  sms: "Hi {{first_name}}, this is West Clinical Research. A new study has opened that may be a fit for you. Want to hear more? Reply YES and we'll give you a call. Reply STOP to opt out.",
  email: 'Hi {{first_name}},\n\nThis is West Clinical Research. A new study has opened that may be a fit for you…',
};

const categoryLabel = (cat: string) => (cat === 'study_details' ? 'Study details' : cat[0].toUpperCase() + cat.slice(1));

/** Local datetime-input value for now+deltaMin, minute-floored. */
function localDatetimeValue(deltaMin: number): string {
  const d = new Date(Date.now() + deltaMin * 60000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ExportModal({ run, searchName, studies, team, onClose }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [error, setError] = useState<string | null>(null);

  // Destination + attach (§6.6).
  const crmStudies = useMemo(
    () => studies.filter((s) => Boolean(s.id)).sort((a, b) => String(a.internal_title || a.title || a.slug).localeCompare(String(b.internal_title || b.title || b.slug))),
    [studies],
  );
  const [studyId, setStudyId] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);
  const [attachOn, setAttachOn] = useState(true);
  // move_new (default): move them to this study as new leads, the search as the lead source; any
  // other study they were on keeps them at the status they had (exportAttach.planMove).
  const [setConsidering, setSetConsidering] = useState<'move_new' | 'off'>('move_new');
  const [assignOwners, setAssignOwners] = useState<string>('off'); // off | round_robin | user:<id>
  const [addTag, setAddTag] = useState(true);
  const defaultTag = useMemo(
    () => `search:${searchName ? slugify(searchName) : `run-${run.id.slice(0, 8)}`}` || `search:run-${run.id.slice(0, 8)}`,
    [searchName, run.id],
  );
  const [tag, setTag] = useState(defaultTag);

  // Campaign (§6.7) — collapsed off by default.
  const [campaignOn, setCampaignOn] = useState(false);
  const [channel, setChannel] = useState<'sms' | 'email'>('sms');
  // The message: the sender's own words, raw with their {{merge fields}}. A saved template only
  // fills the box; what is in the box is what sends.
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [startedFrom, setStartedFrom] = useState<{ title: string; body: string } | null>(null);
  const [undo, setUndo] = useState<{ body: string; subject: string } | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const insertInto = useRef<'body' | 'subject'>('body');
  const [previewIdx, setPreviewIdx] = useState(0);
  const [startAt, setStartAt] = useState(() => localDatetimeValue(60));
  const [pace, setPace] = useState(120);
  const [skipOptedOut, setSkipOptedOut] = useState(true);
  const [skipNoConsent, setSkipNoConsent] = useState(true); // O-1: default ON, per-campaign override
  const [skipRecent, setSkipRecent] = useState(true);
  const [recentDays, setRecentDays] = useState(7);
  const [skipActive, setSkipActive] = useState(true);
  // Default ON; unticked, the texts reach archived / closed-stage leads too (the send tick honours it).
  const [skipClosed, setSkipClosed] = useState(true);

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  useEffect(() => {
    if (!campaignOn) return;
    api.get('/api/crm/templates', { params: { ...(studyId ? { study_id: studyId } : {}), channel } })
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [campaignOn, studyId, channel]);
  const templatesByCategory = useMemo(() => {
    const groups: Record<string, MessageTemplate[]> = {};
    for (const t of templates) {
      (groups[t.category || 'general'] ||= []).push(t);
    }
    return groups;
  }, [templates]);

  /** Fill the box from a saved template; typed words it replaces can be brought back once. */
  const startFromTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const hadText = Boolean(body.trim() || (channel === 'email' && subject.trim()));
    setUndo(hadText ? { body, subject } : null);
    setBody(t.body || '');
    if (channel === 'email') setSubject(t.subject || '');
    setStartedFrom({ title: t.title, body: t.body || '' });
  };
  const editBody = (v: string) => { setBody(v); setUndo(null); };
  const editSubject = (v: string) => { setSubject(v); setUndo(null); };
  const insertVariable = (token: string) => {
    if (channel === 'email' && insertInto.current === 'subject') {
      insertAtCursor(subjectRef.current, subject, editSubject, token);
    } else {
      insertAtCursor(bodyRef.current, body, editBody, token);
    }
  };

  // Live preflight (§7.5): one dry_run call recomputes everything, debounced.
  const [preflight, setPreflight] = useState<ExportPreflight | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  const buildParams = useCallback(() => ({
    study_id: studyId,
    only_new: onlyNew,
    attach: attachOn ? {
      set_considering: setConsidering,
      assign_owners: assignOwners,
      add_tag: addTag,
      ...(addTag && tag ? { tag } : {}),
    } : null,
    // The message itself is added at launch only (see the header).
    campaign: campaignOn ? {
      channel,
      ...(startAt ? { start_at: new Date(startAt).toISOString() } : {}),
      pace_per_hour: pace,
      skip_rules: {
        opted_out: skipOptedOut,
        no_consent_basis: skipNoConsent,
        recently_contacted: skipRecent,
        recently_contacted_days: recentDays,
        active_elsewhere: skipActive,
        closed_lead: skipClosed,
      },
    } : null,
  }), [studyId, onlyNew, attachOn, setConsidering, assignOwners, addTag, tag, campaignOn,
    channel, startAt, pace, skipOptedOut, skipNoConsent, skipRecent, recentDays, skipActive, skipClosed]);

  const preflightSeq = useRef(0);
  useEffect(() => {
    if (!studyId || (!attachOn && !campaignOn)) { setPreflight(null); setPreflightError(null); return; }
    const seq = ++preflightSeq.current;
    const timer = setTimeout(async () => {
      setPreflightLoading(true);
      try {
        const res = await api.post(`/api/crm/search-runs/${run.id}/export`, { dry_run: true, params: buildParams() });
        if (seq !== preflightSeq.current) return;
        setPreflight(res.data?.preflight || null);
        setPreflightError(null);
      } catch (e: any) {
        if (seq !== preflightSeq.current) return;
        setPreflight(null);
        setPreflightError(errMsg(e));
      } finally {
        if (seq === preflightSeq.current) setPreflightLoading(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [run.id, studyId, attachOn, campaignOn, buildParams]);

  const teamName = useCallback(
    (userId: string) => team.find((m) => m.id === userId)?.name || 'A former team member',
    [team],
  );

  // Launch + progress.
  const [launching, setLaunching] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportRow, setExportRow] = useState<SearchExport | null>(null);
  const [progress, setProgress] = useState<ExportProgressInfo | null>(null);
  const [items, setItems] = useState<ExportReceiptItem[]>([]);
  const [canceling, setCanceling] = useState(false);

  const launch = async () => {
    setLaunching(true);
    setError(null);
    try {
      const base = buildParams();
      const params = base.campaign
        ? { ...base, campaign: { ...base.campaign, body, ...(channel === 'email' ? { subject } : {}) } }
        : base;
      const res = await api.post(`/api/crm/search-runs/${run.id}/export`, { params });
      setExportId(res.data?.export_id || null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLaunching(false);
    }
  };

  const refreshExport = useCallback(async () => {
    if (!exportId) return null;
    try {
      const res = await api.get(`/api/crm/search-exports/${exportId}`, { params: { limit: 100 } });
      setExportRow(res.data?.export || null);
      setProgress(res.data?.progress || null);
      setItems(res.data?.items || []);
      return res.data;
    } catch (e: any) {
      setError(errMsg(e));
      return null;
    }
  }, [exportId]);

  useEffect(() => {
    if (!exportId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const data = await refreshExport();
      if (!alive) return;
      const status = data?.export?.status;
      const remaining = data?.progress?.scheduled_remaining ?? 0;
      if (status === 'pending' || status === 'running') timer = setTimeout(tick, 1500);
      else if (remaining > 0) timer = setTimeout(tick, 5000); // sends still draining
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [exportId, refreshExport]);

  const cancelExport = async () => {
    if (!exportId) return;
    setCanceling(true);
    try {
      await api.patch(`/api/crm/search-exports/${exportId}/cancel`);
      await refreshExport();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setCanceling(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const campaignPf = preflight?.campaign;
  const attachPf = preflight?.attach;
  const chosenStudy = studies.find((s) => s.id === studyId) || null;
  const msgCheck = useMemo(
    () => checkCampaignMessage({ channel, body, subject, studyTitle: chosenStudy?.title || null }),
    [channel, body, subject, chosenStudy?.title],
  );
  const messageBlocked = campaignOn && msgCheck.errors.length > 0;
  const capBlocked = campaignOn && Boolean(campaignPf?.cap_exceeded);
  const step1Ready = Boolean(studyId) && (attachOn || campaignOn);
  const confirmReady = step1Ready && !preflightLoading && Boolean(preflight)
    && !messageBlocked && !capBlocked && !preflightError;

  // Preview: the people who will get it, as the preflight says they'll read it.
  const previewPeople: CampaignPreviewPerson[] = campaignPf?.preview?.people || [];
  const previewAt = Math.min(previewIdx, Math.max(0, previewPeople.length - 1));
  const previewPerson = previewPeople[previewAt] || null;
  const previewNotes: string[] = [];
  if (campaignPf?.preview && body.trim()) {
    const { no_first_name: noFirst, not_on_study: elsewhere } = campaignPf.preview;
    const people = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'person' : 'people'}`;
    if (msgCheck.usesFirstName && noFirst > 0) {
      previewNotes.push(`${people(noFirst)} ${noFirst === 1 ? 'has' : 'have'} no first name on file, so {{first_name}} will be blank for them.`);
    }
    if (msgCheck.usesStudy && elsewhere > 0) {
      previewNotes.push(`${people(elsewhere)} will be on a different study when it sends, so {{study_title}} will name that study instead of this one.`);
    }
  }

  const skipRows: Array<{ key: string; on: boolean; toggle?: (v: boolean) => void; fixed?: boolean }> = [
    { key: 'opted_out', on: skipOptedOut, toggle: setSkipOptedOut },
    { key: 'no_channel', on: true, fixed: true },
    ...(channel === 'sms' ? [{ key: 'no_consent_basis', on: skipNoConsent, toggle: setSkipNoConsent }] : []),
    { key: 'recently_contacted', on: skipRecent, toggle: setSkipRecent },
    { key: 'active_elsewhere', on: skipActive, toggle: setSkipActive },
    { key: 'closed_lead', on: skipClosed, toggle: setSkipClosed },
    // Wrong numbers are always skipped by the server — a known stranger is never texted.
    ...ALWAYS_ON_SKIPS
      .filter((key) => campaignPf?.skips && key in campaignPf.skips)
      .map((key) => ({ key, on: true, fixed: true })),
  ];

  const stepChip = (n: 1 | 2 | 3, title: string) => (
    <button
      type="button"
      onClick={() => { if (!exportId && (n === 1 || step1Ready)) setStep(n); }}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
        step === n
          ? 'bg-primary-600 text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
      }`}
    >
      <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${step === n ? 'bg-white/25' : 'bg-white dark:bg-gray-800'}`}>{n}</span>
      {title}
    </button>
  );

  return (
    <div className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/40 p-4`} onClick={onClose} role="presentation">
      <div
        className="flex max-h-[88vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Attach leads to a study"
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-3 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
              Attach {run.final_total?.toLocaleString() ?? '—'} lead{run.final_total === 1 ? '' : 's'} to a study
            </h2>
            {!exportId && (
              <div className="mt-1.5 flex items-center gap-1.5">
                {stepChip(1, 'Study')}
                {stepChip(2, 'Message')}
                {stepChip(3, 'Review')}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <Alert tone="error" size="sm" icon={false} className="mb-3">{error}</Alert>
          )}

          {/* ---- Progress view (after launch) ---- */}
          {exportId ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                {exportRow?.status === 'complete' ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                  : exportRow?.status === 'failed' ? <AlertTriangle className="h-4 w-4 text-rose-500" />
                    : <Loader2 className="h-4 w-4 animate-spin text-primary-500" />}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {exportRow?.status === 'complete' ? 'Done'
                    : exportRow?.status === 'failed' ? "This didn't finish"
                      : exportRow?.status === 'canceled' ? 'Cancelled'
                        : 'Working on it…'}
                </span>
                {exportRow?.updated_at && <span className="text-xs text-gray-400">updated {relTime(exportRow.updated_at)}</span>}
              </div>
              {exportRow?.error && (
                <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{exportErrorText(exportRow.error)}</p>
              )}

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  ['Attached', progress?.receipts?.attached],
                  ['Messages scheduled', progress?.receipts?.scheduled],
                  ['Skipped', progress?.receipts?.skipped],
                  ['Sent so far', progress?.sent_so_far],
                ].map(([label, n]) => (
                  <div key={label as string} className="rounded-lg border border-gray-200 px-2.5 py-1.5 dark:border-gray-700">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
                    <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">{(n as number | undefined)?.toLocaleString() ?? 0}</p>
                  </div>
                ))}
              </div>
              {(progress?.scheduled_remaining ?? 0) > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {progress!.scheduled_remaining.toLocaleString()} message{progress!.scheduled_remaining === 1 ? '' : 's'} still waiting to send.
                  Quiet hours, opt-outs and consent are checked again as each one sends.
                </p>
              )}

              <ul className="max-h-64 divide-y divide-gray-50 overflow-y-auto rounded-lg border border-gray-100 dark:divide-gray-700/50 dark:border-gray-700">
                {items.map((it) => {
                  const w = receiptWords(it, teamName);
                  return (
                    <li key={`${it.contact_id}:${it.outcome}`} className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
                      <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-100">
                        {it.contact?.full_name || `${it.contact?.first_name || ''} ${it.contact?.last_name || ''}`.trim() || it.contact?.phone_e164 || 'Unnamed lead'}
                      </span>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${w.tone}`}>{w.pill}</span>
                      {w.detail && <span className="hidden max-w-[14rem] truncate text-[10px] text-gray-400 sm:inline" title={w.detail}>{w.detail}</span>}
                    </li>
                  );
                })}
                {items.length === 0 && <li className="px-3 py-4 text-center text-xs text-gray-400">Nothing yet…</li>}
              </ul>
            </div>
          ) : (
            <>
              {/* ---- Step 1: Destination ---- */}
              {step === 1 && (
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Study</label>
                    <select value={studyId} onChange={(e) => setStudyId(e.target.value)} aria-label="Study" className={fieldCls}>
                      <option value="">Pick a study…</option>
                      {crmStudies.map((s) => (
                        <option key={s.id!} value={s.id!}>{s.internal_title || s.title || s.slug}</option>
                      ))}
                    </select>
                  </div>
                  {(run.new_matches ?? 0) > 0 && (
                    <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
                      <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} className={checkCls} />
                      Only the {run.new_matches} {run.new_matches === 1 ? 'person' : 'people'} new since the last run
                    </label>
                  )}

                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      <input type="checkbox" checked={attachOn} onChange={(e) => setAttachOn(e.target.checked)} className={checkCls} />
                      Attach them to the study
                    </label>
                    {attachOn && (
                      <div className="mt-2 space-y-2.5 pl-6">
                        <div>
                          <label className={labelCls}>Study they're being considered for</label>
                          <select value={setConsidering} onChange={(e) => setSetConsidering(e.target.value as 'move_new' | 'off')} aria-label="Considering mode" className={fieldCls}>
                            <option value="move_new">Move them to this study as new leads</option>
                            <option value="off">Leave it as it is</option>
                          </select>
                          {setConsidering === 'move_new' && (
                            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                              They start at New on this study, with this search as the lead source. Closed and archived leads are reopened.
                              Anyone being considered for another study stays on that study's list with the status they had.
                            </p>
                          )}
                        </div>
                        <div>
                          <label className={labelCls}>Owner</label>
                          <select value={assignOwners} onChange={(e) => setAssignOwners(e.target.value)} aria-label="Owner assignment" className={fieldCls}>
                            <option value="off">Don't change owners</option>
                            <option value="round_robin">Share between the study's recruiters (only leads with no owner)</option>
                            {team.length > 0 && (
                              <optgroup label="Give them all to (only leads with no owner)">
                                {team.map((m) => <option key={m.id} value={`user:${m.id}`}>{m.name}</option>)}
                              </optgroup>
                            )}
                          </select>
                          {assignOwners === 'round_robin' && attachPf?.roster_size === 0 && (
                            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">This study has no active recruiters, so nobody will be given an owner.</p>
                          )}
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
                            <input type="checkbox" checked={addTag} onChange={(e) => setAddTag(e.target.checked)} className={checkCls} />
                            Tag them
                          </label>
                          {addTag && (
                            <input
                              value={tag}
                              onChange={(e) => setTag(e.target.value)}
                              aria-label="List tag"
                              className={`${fieldCls} font-mono text-xs`}
                              placeholder={defaultTag}
                            />
                          )}
                        </div>
                        {attachPf && (
                          <ul className="space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                            {attachMoveLines(attachPf).map((line) => <li key={line}>{line}</li>)}
                            <li>
                              Will tag {attachPf.to_tag.toLocaleString()}
                              {attachPf.set_considering !== 'move_new' ? ` · set the study on ${attachPf.to_set_considering.toLocaleString()}` : ''}
                              {' '}· give {attachPf.to_assign_owner.toLocaleString()} an owner
                            </li>
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ---- Step 2: Campaign ---- */}
              {step === 2 && (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    <input type="checkbox" checked={campaignOn} onChange={(e) => setCampaignOn(e.target.checked)} className={checkCls} />
                    Send them a message
                  </label>
                  {!campaignOn && (
                    <p className="pl-6 text-xs text-gray-500 dark:text-gray-400">
                      Optional — without a message, nobody is contacted. Messages go out through the normal
                      sending path: quiet hours, opt-outs and consent are checked again as each one sends.
                    </p>
                  )}
                  {campaignOn && (
                    <div className="space-y-3 pl-6">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className={labelCls}>Channel</label>
                          <select value={channel} onChange={(e) => { setChannel(e.target.value as 'sms' | 'email'); setUndo(null); }} aria-label="Channel" className={fieldCls}>
                            <option value="sms">Text message (SMS)</option>
                            <option value="email">Email</option>
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Messages per hour <span className="font-normal text-gray-400">(up to 600)</span></label>
                          <input
                            type="number" min={1} max={600} value={pace}
                            onChange={(e) => setPace(Math.max(1, Math.min(600, parseInt(e.target.value, 10) || 120)))}
                            aria-label="Pace per hour" className={fieldCls}
                          />
                        </div>
                      </div>
                      {templates.length > 0 && (
                        <div>
                          <label htmlFor="export-template" className={labelCls}>Start from a saved template <span className="font-normal text-gray-400">(optional)</span></label>
                          <select
                            id="export-template"
                            value=""
                            onChange={(e) => startFromTemplate(e.target.value)}
                            className={fieldCls}
                          >
                            <option value="">Pick one to fill the message below…</option>
                            {Object.entries(templatesByCategory).map(([cat, list]) => (
                              <optgroup key={cat} label={categoryLabel(cat)}>
                                {list.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </div>
                      )}

                      {channel === 'email' && (
                        <div>
                          <label htmlFor="export-subject" className={labelCls}>Subject</label>
                          <input
                            id="export-subject"
                            ref={subjectRef}
                            value={subject}
                            onChange={(e) => editSubject(e.target.value)}
                            onFocus={() => { insertInto.current = 'subject'; }}
                            placeholder="A new study that may be a fit for you"
                            className={fieldCls}
                          />
                        </div>
                      )}

                      <div>
                        <div className="flex items-end justify-between gap-2">
                          <label htmlFor="export-message" className={labelCls}>Message</label>
                          <VariablePicker items={CAMPAIGN_VARIABLES} onInsert={insertVariable} menuAlign="right" />
                        </div>
                        <textarea
                          id="export-message"
                          ref={bodyRef}
                          value={body}
                          onChange={(e) => editBody(e.target.value)}
                          onFocus={() => { insertInto.current = 'body'; }}
                          rows={channel === 'email' ? 7 : 5}
                          placeholder={MESSAGE_PLACEHOLDER[channel]}
                          aria-describedby="export-message-count export-message-rules"
                          className={`${fieldCls} resize-y leading-snug`}
                        />
                        <p id="export-message-count" className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                          <span>
                            {msgCheck.chars.toLocaleString()} characters
                            {channel === 'sms' && msgCheck.chars > 0 && ` · ${msgCheck.segments === 1 ? 'fits in 1 text' : `${msgCheck.segments} texts long`}`}
                          </span>
                          {startedFrom && (
                            <span>· {body === startedFrom.body ? 'from' : 'started from'} “{startedFrom.title}”</span>
                          )}
                          {undo && (
                            <button
                              type="button"
                              onClick={() => { setBody(undo.body); setSubject(undo.subject); setUndo(null); setStartedFrom(null); }}
                              className="font-medium text-primary-700 hover:underline dark:text-primary-400"
                            >
                              Undo — bring back what I wrote
                            </button>
                          )}
                        </p>
                        <div id="export-message-rules" aria-live="polite">
                          {body.trim() !== '' && msgCheck.errors.length > 0 && (
                            <ul className="mt-1 space-y-0.5 text-[11px] text-rose-600 dark:text-rose-400">
                              {msgCheck.errors.map((m) => <li key={m}>{m}</li>)}
                            </ul>
                          )}
                          {msgCheck.warnings.map((w) => <p key={w} className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">{w}</p>)}
                        </div>
                      </div>

                      <MessagePreview
                        channel={channel}
                        body={body}
                        subject={subject}
                        people={previewPeople}
                        at={previewAt}
                        onAt={setPreviewIdx}
                        hasCounts={Boolean(campaignPf)}
                        notes={previewNotes}
                      />
                      <div>
                        <label className={labelCls}>Start</label>
                        <input
                          type="datetime-local" value={startAt} min={localDatetimeValue(0)} max={localDatetimeValue(14 * 24 * 60)}
                          onChange={(e) => setStartAt(e.target.value)} aria-label="Start time" className={fieldCls}
                        />
                        <p className="mt-1 text-[11px] text-gray-400">Up to 14 days out. Messages go out from here at the rate above.</p>
                      </div>

                      <div className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-200">Who won't get it {preflightLoading && <Loader2 className="ml-1 inline h-3 w-3 animate-spin text-gray-400" />}</p>
                        <ul className="mt-1.5 space-y-1.5">
                          {skipRows.map(({ key, on, toggle, fixed }) => (
                            <li key={key} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox" checked={on} disabled={fixed}
                                onChange={(e) => toggle?.(e.target.checked)}
                                aria-label={SKIP_LABELS[key]}
                                className={`${checkCls} ${fixed ? 'opacity-60' : ''}`}
                              />
                              <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-100">
                                {SKIP_LABELS[key]}
                                {key === 'recently_contacted' && (
                                  <>
                                    {' '}within{' '}
                                    <input
                                      type="number" min={1} max={90} value={recentDays}
                                      onChange={(e) => setRecentDays(Math.max(1, Math.min(90, parseInt(e.target.value, 10) || 7)))}
                                      aria-label="Recently contacted days"
                                      className="w-12 rounded border border-gray-300 bg-white px-1 py-0.5 text-center text-xs dark:border-gray-600 dark:bg-gray-900"
                                    /> days
                                  </>
                                )}
                                {key === 'no_consent_basis' && (
                                  <span className="ml-1 text-[11px] text-gray-400">(untick only if you know they agreed to texts)</span>
                                )}
                                {key === 'closed_lead' && (
                                  <span className="ml-1 text-[11px] text-gray-400">(untick to text old leads we closed or archived too)</span>
                                )}
                                {fixed && <span className="ml-1 text-[11px] text-gray-400">(always on)</span>}
                              </span>
                              <span className="tabular-nums text-xs font-medium text-gray-500 dark:text-gray-400">
                                {on ? (campaignPf?.skips?.[key] ?? '—') : 'off'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {campaignPf && (
                        <p className={`text-xs ${campaignPf.cap_exceeded ? 'font-medium text-rose-600 dark:text-rose-400' : campaignPf.warn_large ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          Will schedule {campaignPf.to_schedule.toLocaleString()} {channel === 'sms' ? 'texts' : 'emails'}
                          {campaignPf.cap_exceeded
                            ? ` — more than the ${campaignPf.cap.toLocaleString()} allowed at once. Narrow the search and do it in two goes.`
                            : campaignPf.warn_large ? ' — that’s a lot of people; double-check the list.' : ''}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ---- Step 3: Confirm ---- */}
              {step === 3 && (
                <div className="space-y-3">
                  {preflightLoading || !preflight ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Adding it all up…</div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/40">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {[
                            attachOn && preflight.attach ? `Attach ${preflight.selected.toLocaleString()}` : null,
                            campaignOn && campaignPf ? `Schedule ${campaignPf.to_schedule.toLocaleString()} ${channel === 'sms' ? 'texts' : 'emails'}` : null,
                            campaignOn && campaignPf ? `Skip ${(campaignPf.selected - campaignPf.to_schedule).toLocaleString()}` : null,
                          ].filter(Boolean).join(' · ')}
                          {' '}→ {preflight.study.title}
                        </p>
                        <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                          <li>{preflight.selected.toLocaleString()} {preflight.selected === 1 ? 'person' : 'people'} on the list{onlyNew ? ' (new ones only)' : ''}{preflight.missing > 0 ? ` · ${preflight.missing} merged or removed since the run` : ''}</li>
                          {attachOn && attachPf && attachMoveLines(attachPf).map((line) => <li key={line}>{line}</li>)}
                          {attachOn && attachPf && (
                            <li>
                              Tag {attachPf.to_tag.toLocaleString()}{attachPf.tag ? ` (${attachPf.tag})` : ''}
                              {attachPf.set_considering !== 'move_new' ? ` · set the study on ${attachPf.to_set_considering.toLocaleString()}` : ''}
                              {' '}· give {attachPf.to_assign_owner.toLocaleString()} an owner
                            </li>
                          )}
                          {campaignOn && campaignPf && Object.entries(campaignPf.skips).filter(([, n]) => (n as number) > 0).map(([k, n]) => (
                            <li key={k}>Skipped — {exportSkipLabel(k).toLowerCase()}: {(n as number).toLocaleString()}</li>
                          ))}
                          {campaignOn && campaignPf && campaignPf.first_send_at && (
                            <li>First message {dateTime(campaignPf.first_send_at)}{campaignPf.last_send_at && campaignPf.to_schedule > 1 ? ` · last ${dateTime(campaignPf.last_send_at)}` : ''} · safety checks run again as each one sends</li>
                          )}
                        </ul>
                      </div>
                      {campaignOn && body.trim() && (
                        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                          <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                            <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                            {previewPerson ? `How ${previewPersonName(previewPerson)} will see it` : 'The message'}
                          </p>
                          <MessageBody
                            channel={channel}
                            subject={previewPerson ? renderCampaignMessage(subject, previewPerson) : subject}
                            body={previewPerson ? renderCampaignMessage(body, previewPerson) : body}
                          />
                        </div>
                      )}
                      {attachOn && (attachPf?.warnings || []).map((w) => (
                        <p key={w} className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">{exportWarningText(w)}</p>
                      ))}
                      {campaignOn && campaignPf?.warn_large && !campaignPf.cap_exceeded && (
                        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                          More than 500 people — make sure this list is exactly who you mean.
                        </p>
                      )}
                      {capBlocked && (
                        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                          That's more than the {campaignPf!.cap.toLocaleString()} people allowed at once — narrow the search first.
                        </p>
                      )}
                      {messageBlocked && (
                        <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                          <p>Fix the message before launching (step 2):</p>
                          <ul className="mt-0.5 list-disc pl-4 font-normal">
                            {msgCheck.errors.map((m) => <li key={m}>{m}</li>)}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {preflightError && step !== 2 && (
                <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{preflightError}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-5 py-3 dark:border-gray-700">
          {exportId ? (
            <>
              <div>
                {(progress?.scheduled_remaining ?? 0) > 0 && (
                  <button
                    type="button" onClick={cancelExport} disabled={canceling}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-700 dark:text-rose-400 dark:hover:bg-rose-900/20"
                  >
                    {canceling && <Loader2 className="h-4 w-4 animate-spin" />}
                    Cancel remaining sends
                  </button>
                )}
              </div>
              <button type="button" onClick={onClose} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => (step === 1 ? onClose() : setStep((step - 1) as 1 | 2))}
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {step === 1 ? 'Cancel' : 'Back'}
              </button>
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => setStep((step + 1) as 2 | 3)}
                  disabled={!step1Ready}
                  className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={launch}
                  disabled={!confirmReady || launching}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {campaignOn && campaignPf
                    ? `Attach and schedule ${campaignPf.to_schedule.toLocaleString()} ${channel === 'sms' ? 'texts' : 'emails'}`
                    : 'Attach to study'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** The message as it lands: a text bubble, or an email's subject line over its body. */
function MessageBody({ channel, subject, body }: { channel: 'sms' | 'email'; subject: string; body: string }) {
  if (channel === 'email') {
    return (
      <div className="mt-2 rounded-lg border border-gray-200 bg-white text-sm dark:border-gray-700 dark:bg-gray-900">
        <p className="break-words border-b border-gray-100 px-3 py-1.5 font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
          {subject.trim() || <span className="font-normal italic text-gray-400">No subject yet</span>}
        </p>
        <p className="whitespace-pre-wrap break-words px-3 py-2 text-gray-800 dark:text-gray-100">{body}</p>
      </div>
    );
  }
  return (
    <p className="mt-2 max-w-[28rem] whitespace-pre-wrap break-words rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-900 dark:bg-gray-700 dark:text-gray-100">
      {body}
    </p>
  );
}

/**
 * The Message step's preview: the message filled in for the people the preflight says will get it
 * (after the attach half moves them), one at a time, with the notes that apply to the rest.
 */
function MessagePreview({ channel, body, subject, people, at, onAt, hasCounts, notes }: {
  channel: 'sms' | 'email';
  body: string;
  subject: string;
  people: CampaignPreviewPerson[];
  at: number;
  onAt: (i: number) => void;
  /** The preflight has answered (so an empty `people` means nobody will get it). */
  hasCounts: boolean;
  notes: string[];
}) {
  const person = people[at] || null;
  const navBtn = 'inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700';
  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700" aria-label="Message preview">
      <div className="flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
          <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span className="truncate">{person ? `Preview as ${previewPersonName(person)}` : 'Preview'}</span>
        </p>
        {people.length > 1 && (
          <div className="flex flex-shrink-0 items-center gap-0.5">
            <button type="button" onClick={() => onAt(at - 1)} disabled={at === 0} aria-label="Previous person" className={navBtn}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400">{at + 1} of {people.length}</span>
            <button type="button" onClick={() => onAt(at + 1)} disabled={at >= people.length - 1} aria-label="Next person" className={navBtn}>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      {!body.trim() ? (
        <p className="mt-1 text-[11px] text-gray-400">Write the message above and it shows here the way each person will get it.</p>
      ) : (
        <>
          <MessageBody
            channel={channel}
            subject={person ? renderCampaignMessage(subject, person) : subject}
            body={person ? renderCampaignMessage(body, person) : body}
          />
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            {person
              ? 'Names and the study fill in for each person when it sends.'
              : hasCounts
                ? 'Nobody on the list gets this with the settings below, so there is no one to preview it as.'
                : 'Finding people on the list to preview it as…'}
          </p>
          {notes.map((n) => <p key={n} className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">{n}</p>)}
        </>
      )}
    </div>
  );
}
